import apiClient from './client';

// ===== TASKS =====

/**
 * Get all tasks with filters and pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 20)
 * @param {string} params.search - Search query
 * @param {string} params.status - Filter by status
 * @param {string} params.priority - Filter by priority
 * @param {number} params.assigned_to - Filter by assigned user
 * @param {number} params.assigned_by - Filter by creator
 * @param {string} params.category - Filter by category
 * @param {string} params.from_date - Start date filter
 * @param {string} params.to_date - End date filter
 * @param {boolean} params.is_deleted - Include deleted tasks
 * @param {string} params.sort_by - Sort field (default: created_at)
 * @param {string} params.sort_order - Sort order: ASC or DESC (default: DESC)
 */
export const getTasks = async (params = {}) => {
  const response = await apiClient.get('/tasks', {
    params,
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
  return response.data;
};

/**
 * Get single task by ID
 */
export const getTaskById = async (id) => {
  const response = await apiClient.get(`/tasks/${id}`);
  return response.data;
};

/**
 * Get current user's assigned tasks
 */
export const getMyTasks = async (params = {}) => {
  const response = await apiClient.get('/tasks/my-tasks', {
    params,
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
  return response.data;
};

/**
 * Get tasks created by current user
 */
export const getCreatedTasks = async (params = {}) => {
  const response = await apiClient.get('/tasks/created-by-me', {
    params,
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
  return response.data;
};

/**
 * Get overdue tasks
 */
export const getOverdueTasks = async () => {
  const response = await apiClient.get('/tasks/overdue', {
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
  return response.data;
};

/**
 * Get upcoming tasks
 * @param {number} days - Number of days to look ahead (default: 7)
 */
export const getUpcomingTasks = async (days = 7) => {
  const response = await apiClient.get('/tasks/upcoming', {
    params: { days },
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
  return response.data;
};

/**
 * Get task statistics
 * @param {Object} params - Filter parameters
 */
export const getTaskStatistics = async (params = {}) => {
  const response = await apiClient.get('/tasks/statistics', {
    params,
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
  return response.data;
};

/**
 * Create new task
 * @param {Object} taskData - Task data
 * @param {string} taskData.title - Task title (required)
 * @param {string} taskData.description - Task description (required)
 * @param {number} taskData.assigned_to - User ID to assign task to (required)
 * @param {string} taskData.due_date - Due date in YYYY-MM-DD format (required)
 * @param {string} taskData.due_time - Due time in HH:MM format (optional)
 * @param {string} taskData.priority - Priority: low, medium, high, urgent (default: medium)
 * @param {string} taskData.category - Task category (optional)
 */
export const createTask = async (taskData) => {
  const response = await apiClient.post('/tasks', taskData);
  return response.data;
};

/**
 * Update task
 * @param {number} id - Task ID
 * @param {Object} taskData - Updated task data
 */
export const updateTask = async (id, taskData) => {
  const response = await apiClient.put(`/tasks/${id}`, taskData);
  return response.data;
};

/**
 * Update task status
 * @param {number} id - Task ID
 * @param {string} status - New status
 * @param {string} notes - Optional notes
 */
export const updateTaskStatus = async (id, status, notes = null) => {
  const response = await apiClient.patch(`/tasks/${id}/status`, { status, notes });
  return response.data;
};

/**
 * Reassign task to another user
 * @param {number} id - Task ID
 * @param {number} assigned_to - New assignee user ID
 * @param {string} notes - Optional notes
 */
export const reassignTask = async (id, assigned_to, notes = null) => {
  const response = await apiClient.patch(`/tasks/${id}/assign`, { assigned_to, notes });
  return response.data;
};

/**
 * Delete task (soft delete)
 * @param {number} id - Task ID
 */
export const deleteTask = async (id) => {
  const response = await apiClient.delete(`/tasks/${id}`);
  return response.data;
};

/**
 * Restore deleted task from recycle bin
 * @param {number} id - Task ID
 */
export const restoreTask = async (id) => {
  const response = await apiClient.post(`/tasks/${id}/restore`);
  return response.data;
};

/**
 * Get task history
 * @param {number} id - Task ID
 */
export const getTaskHistory = async (id) => {
  const response = await apiClient.get(`/tasks/${id}/history`);
  return response.data;
};

// ===== NOTIFICATIONS =====

/**
 * Get user notifications
 * @param {Object} params - Query parameters
 * @param {boolean} params.is_read - Filter by read status
 * @param {number} params.limit - Items per page (default: 50)
 * @param {number} params.offset - Offset for pagination
 * @param {string} params.notification_type - Filter by notification type
 */
export const getNotifications = async (params = {}) => {
  const response = await apiClient.get('/tasks/notifications', {
    params,
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
  return response.data;
};

/**
 * Mark notification as read
 * @param {number} id - Notification ID
 */
export const markNotificationAsRead = async (id) => {
  const response = await apiClient.patch(`/tasks/notifications/${id}/read`);
  return response.data;
};

/**
 * Mark all notifications as read
 */
export const markAllNotificationsAsRead = async () => {
  const response = await apiClient.patch('/tasks/notifications/read-all');
  return response.data;
};

// ===== REMINDERS =====

/**
 * Get user reminders
 */
export const getReminders = async () => {
  const response = await apiClient.get('/tasks/reminders', {
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
  return response.data;
};

/**
 * Acknowledge reminder
 * @param {number} id - Reminder ID
 */
export const acknowledgeReminder = async (id) => {
  const response = await apiClient.patch(`/tasks/reminders/${id}/acknowledge`);
  return response.data;
};

/**
 * Snooze reminder
 * @param {number} id - Reminder ID
 * @param {number} snooze_duration - Snooze duration in minutes (30, 60, or 1440)
 */
export const snoozeReminder = async (id, snooze_duration) => {
  const response = await apiClient.patch(`/tasks/reminders/${id}/snooze`, { snooze_duration });
  return response.data;
};

// ===== UTILITY FUNCTIONS =====

/**
 * Get task status color
 * @param {string} status - Task status
 * @returns {string} Color class or hex code
 */
export const getTaskStatusColor = (status) => {
  const statusColors = {
    pending: '#6B7280',
    in_progress: '#3B82F6',
    on_hold: '#F59E0B',
    completed: '#10B981',
    cancelled: '#EF4444',
    closed: '#6B7280'
  };
  return statusColors[status] || '#6B7280';
};

/**
 * Get task priority color
 * @param {string} priority - Task priority
 * @returns {string} Color class or hex code
 */
export const getTaskPriorityColor = (priority) => {
  const priorityColors = {
    low: '#6B7280',
    medium: '#3B82F6',
    high: '#F59E0B',
    urgent: '#EF4444'
  };
  return priorityColors[priority] || '#3B82F6';
};

/**
 * Format task number
 * @param {string} taskNumber - Task number (e.g., TASK-2025-00001)
 * @returns {string} Formatted task number
 */
export const formatTaskNumber = (taskNumber) => {
  return taskNumber || 'N/A';
};

/**
 * Check if task is overdue
 * @param {string} dueDate - Due date
 * @param {string} dueTime - Due time (optional)
 * @param {string} status - Task status
 * @returns {boolean} True if overdue
 */
export const isTaskOverdue = (dueDate, dueTime, status) => {
  if (!dueDate || ['completed', 'cancelled', 'closed'].includes(status)) {
    return false;
  }

  const now = new Date();
  const due = new Date(dueDate);

  if (dueTime) {
    const [hours, minutes] = dueTime.split(':');
    due.setHours(parseInt(hours), parseInt(minutes));
  } else {
    due.setHours(23, 59, 59);
  }

  return now > due;
};

/**
 * Get days until due date
 * @param {string} dueDate - Due date
 * @returns {number} Days until due (negative if overdue)
 */
export const getDaysUntilDue = (dueDate) => {
  if (!dueDate) return null;

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);

  const diffTime = due - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};
